import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { Spinner } from '../../ui/Spinner';
import { useAccount } from '../../contexts/AccountContext';
import { founder } from '../../data/content';
import { btnPrimary, inputCls, labelCls } from '../../ui/styles';
import { ease } from '../../core/format/motion';
import { PASSWORD_HINT, PASSWORD_MAX, passwordProblem } from '../../core/rules/password';
import type { AccountOrder } from '../../core/types/account';
type Props = {
  email: string;
  firstName: string;
  orderId: string;
  order?: AccountOrder;
  subscribeIds?: string[];
};
const ERROR_ID = 'after-password-error';
const HINT_ID = 'after-password-hint';
const ORDERS_PATH = '/account/bestellingen';
const benefits = ['Zien waar je pakket is', 'Een seintje als een product bijna op is', 'Later opnieuw bestellen in één tik'];

/**
 * De volgende stap na de betaling, als duidelijke kaart: ingelogd → naar je bestelling;
 * gast → één veld om een account te maken (de bestelling staat er dan al in).
 * Nooit vóór het betalen: afrekenen blijft altijd zonder account mogelijk.
 */
export function AccountAfterCheckout({
  email,
  firstName,
  orderId,
  order,
  subscribeIds
}: Props) {
  const {
    signedIn,
    register
  } = useAccount();
  const reduce = useReducedMotion();
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const weak = passwordProblem(password, {
      email,
      firstName
    });
    if (weak) {
      setError(weak);
      document.getElementById('after-password')?.focus();
      return;
    }
    setBusy(true);
    setError('');
    const r = await register({
      firstName,
      email,
      password,
      claim: order ? {
        order,
        subscribeIds: subscribeIds ?? []
      } : undefined
    });
    setBusy(false);
    if (r.ok) setDone(true);else setError(r.error ?? 'Dat lukte even niet. Probeer het nog eens.');
  };

  /** Bestelnummer en status: het tastbare bewijs dat je bestelling ergens "staat". */
  const orderStatus = (tone: 'dark' | 'light') => <p className={`inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] ${tone === 'dark' ? 'text-shell/75' : 'text-muted'}`} data-id="element-5665" data-mp="2fky8kizkhtq5">
      <span data-id="element-5666" data-mp="1mwtzrolp90j8">
        Bestelling <span className={tone === 'dark' ? 'text-shell' : 'text-ink'} data-id="element-5667" data-mp="1mw0ocabspmur">{orderId}</span>
      </span>
      <span aria-hidden="true" data-id="element-5668" data-mp="1rvndni4wetjx">·</span>
      <span className="inline-flex items-center gap-1.5" data-id="element-5669" data-mp="1rulpcbyvgak9">
        <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-clay" data-id="element-5670" data-mp="1wv9qvrkq89sq" />
        In behandeling
      </span>
    </p>;
  return <aside aria-labelledby="after-title" data-id="element-5671" data-mp="33ak7r6fxw8y5">
      <AnimatePresence mode="wait" initial={false} data-id="element-5672" data-mp="389wpjcryeptv">
        {signedIn || done ?
      // Het zwaarste blok op de pagina: donker vlak, één lichte knop. Hier ga je hierna heen.
      <motion.div key="account" role={done ? 'status' : undefined} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="bg-ink p-6 text-shell sm:p-8" data-id="element-5673" data-mp="womizvsvj7hg">
            {orderStatus('dark')}
            <div className="mt-4 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between" data-id="element-5674" data-mp="czlqxyqpahlv">
              <div className="max-w-sm" data-id="element-5675" data-mp="8wfrb9y3food">
                <h2 id="after-title" className="font-display text-[1.75rem] font-light leading-tight" data-id="element-5676" data-mp="8zlbcarv80d8">
                  {done ? 'Je account staat klaar, met je bestelling erin.' : 'Volg je bestelling in Mijn omgeving.'}
                </h2>
                <p className="mt-2 text-[15px] leading-relaxed text-shell/75" data-id="element-5677" data-mp="1yb40717h6ohn">
                  Je status en gebruiksadvies op één plek. Zodra {founder.firstName} je pakket verstuurt, verschijnt daar ook de track & trace.
                </p>
              </div>
              <Link to={ORDERS_PATH} className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-shell px-7 py-4 text-[15px] text-ink transition-colors duration-150 ease-soft hover:bg-blush" data-id="element-5678" data-mp="v19cazzs06x2">
                Bekijk je bestelling
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5679" data-mp="7b5c72lsqpat" />
              </Link>
            </div>
            <Link to="/account" className="mt-6 inline-flex py-1 text-[14px] text-shell/70 underline-offset-4 transition-colors duration-150 ease-soft hover:text-shell hover:underline" data-id="element-5680" data-mp="c36w9ap7hkbe">
              Naar het overzicht van Mijn omgeving
            </Link>
          </motion.div> : <motion.form key="form" onSubmit={submit} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease
      }} noValidate className="grid gap-8 bg-shell p-6 ring-1 ring-line sm:grid-cols-2 sm:p-8" data-id="element-5681" data-mp="3kk9zjikxwhjs">
            <div data-id="element-5682" data-mp="31vps3te94sqn">
              {orderStatus('light')}
              <h2 id="after-title" className="mt-4 font-display text-[1.75rem] font-light leading-tight text-ink" data-id="element-5683" data-mp="3fu7dhkgopt5e">
                Volg je bestelling in Mijn omgeving.
              </h2>
              <ul className="mt-4 space-y-2" data-id="element-5684" data-mp="3ou4802pr9m8d">
                {benefits.map(b => <li key={b} className="flex items-start gap-2.5 text-[15px] leading-snug text-ink" data-id="element-5685" data-mp="3ksqxhfbpwuv4">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-5686" data-mp="2erqrg4y01p1" />
                    {b}
                  </li>)}
              </ul>
            </div>
            <div className="sm:border-l sm:border-line sm:pl-8" data-id="element-5687" data-mp="ccfqkkhenze2">
            <p className="text-[13px] text-muted" data-id="element-5688" data-mp="cal2wyer1t6a">
              Je bestelling staat er al in. Account voor <span className="text-ink" data-id="element-5689" data-mp="7brq787ae676">{email}</span>
            </p>
            <label htmlFor="after-password" className={`${labelCls} mt-3`} data-id="element-5690" data-mp="eidlct4ndjds">
              Kies een wachtwoord
            </label>
            <div className="relative" data-id="element-5691" data-mp="je2o15byrgeb">
              <input id="after-password" maxLength={PASSWORD_MAX} type={show ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={e => {
              setPassword(e.target.value);
              if (error && e.target.value.length >= 8) setError('');
            }} aria-invalid={error ? true : undefined} aria-describedby={error ? `${HINT_ID} ${ERROR_ID}` : HINT_ID} className={`${inputCls} pr-12`} data-id="element-5692" data-mp="oeqjq4b50lev" />
              <button type="button" onClick={() => setShow(v => !v)} aria-label={show ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'} aria-pressed={show} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-5693" data-mp="38xmk8f4sv52x">
                {show ? <EyeOffIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5694" data-mp="2wm2c3mdahqmw" /> : <EyeIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5695" data-mp="2rnshpdze2u2c" />}
              </button>
            </div>
            <p id={HINT_ID} className="mt-1.5 text-[13px] text-muted" data-id="element-5696" data-mp="3pc2glk3vp1p8">
              {PASSWORD_HINT}
            </p>
            {error && <p id={ERROR_ID} role="alert" className="mt-2 text-[13px] text-clay" data-id="element-5697" data-mp="3kd7pzvzo68uk">
                {error}
              </p>}
            <button type="submit" disabled={busy} className={`${btnPrimary} mt-5 w-full py-4 text-[15px]`} data-id="element-5698" data-mp="3aeiimx5eg6sk">
              {busy && <Spinner data-id="element-5699" data-mp="23yg8wf7z4o0k" />}
              {busy ? 'Je account wordt gemaakt…' : 'Maak account en volg je bestelling'}
            </button>
            <p className="mt-3 text-center text-[13px] text-muted" data-id="element-5700" data-mp="1yxjxzhp7g1ln">Liever niet? Prima, je bevestiging komt ook zo per mail.</p>
            </div>
          </motion.form>}
      </AnimatePresence>
    </aside>;
}